// Build My Routine render helpers.
// Loaded before build-my-routine.js.

function buildRoutineCardActionButton({ variant, iconHtml, ariaLabel, slotKey, onActivate, allowDisabledTooltip = true }) {
  // Reusable card-action button factory. All three card actions
  // (forward swap, back swap, remove) share the same base classes —
  // `.build-my-routine-card__swap` for the icon button and
  // `.build-my-routine-card__swap-tooltip` for the tooltip — and add a
  // modifier class (`--back`, `--remove`) for positioning and hover.
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'build-my-routine-card__swap'
    + (variant ? ` build-my-routine-card__swap--${variant}` : '');
  btn.setAttribute('aria-label', ariaLabel);

  const icon = document.createElement('span');
  icon.className = 'build-my-routine-card__swap-icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.innerHTML = iconHtml;
  btn.appendChild(icon);

  const tooltip = document.createElement('span');
  tooltip.className = 'build-my-routine-card__swap-tooltip';
  tooltip.setAttribute('role', 'tooltip');
  btn.appendChild(tooltip);

  btn.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (btn.getAttribute('aria-disabled') === 'true') {
      if (allowDisabledTooltip) {
        btn.classList.add('is-tooltip-visible');
        clearTimeout(btn._tooltipTimer);
        btn._tooltipTimer = setTimeout(() => {
          btn.classList.remove('is-tooltip-visible');
        }, 2500);
      }
      return;
    }

    if (typeof onActivate === 'function') {
      onActivate(slotKey);
    }
  });

  return btn;
}

function buildRoutineCardNode({
  product,
  stepName,
  slotLabel,
  slotKey,
  products,
  onSwapSlot,
  onSwapSlotBack,
  onRemoveSlot,
  onSelectFamilyOption
}) {
  const article = document.createElement('article');
  article.className = 'build-my-routine-card is-entering';
  article.dataset.handle = product.handle;
  article.dataset.slot = slotKey || '';

  if (slotKey) {
    article.appendChild(
      buildRoutineCardActionButton({
        variant: '',
        iconHtml: '&#x21BB;',
        ariaLabel: 'Show a similar product for this step',
        slotKey,
        onActivate: onSwapSlot
      })
    );

    article.appendChild(
      buildRoutineCardActionButton({
        variant: 'back',
        iconHtml: '&#x21BA;',
        ariaLabel: 'Go back to the previous product for this step',
        slotKey,
        onActivate: onSwapSlotBack
      })
    );

    article.appendChild(
      buildRoutineCardActionButton({
        variant: 'remove',
        iconHtml: '&times;',
        ariaLabel: 'Remove this step from my routine',
        slotKey,
        onActivate: onRemoveSlot,
        allowDisabledTooltip: false
      })
    );
  }

  const imageLink = document.createElement('a');
  imageLink.className = 'build-my-routine-card__image';
  imageLink.href = product.url || '#';

  if (product.image) {
    const img = document.createElement('img');
    img.src = product.image;
    img.alt = product.title || '';
    img.loading = 'eager';
    img.decoding = 'async';
    imageLink.appendChild(img);
  } else {
    const placeholder = document.createElement('div');
    placeholder.className = 'build-my-routine-card__image-placeholder';
    imageLink.appendChild(placeholder);
  }

  const stepHtml = stepName
    ? `<p class="build-my-routine-card__step">${escapeHtml(stepName)} · ${escapeHtml(slotLabel || '')}</p>`
    : '';

  const content = document.createElement('div');
  content.className = 'build-my-routine-card__content';
  content.innerHTML = `
    ${stepHtml}
    <p class="build-my-routine-card__vendor">${escapeHtml(product.vendor)}</p>
    <h4>${escapeHtml(product.title)}</h4>
    <div class="build-my-routine-card__footer">
      <strong>${formatMoney(product.price_cents)}</strong>
      <a href="${escapeHtml(product.url)}">View product</a>
    </div>
  `;

  content.insertAdjacentHTML('beforeend', buildRoutineFamilySelectorHtml(product, products));

  content.querySelectorAll('[data-routine-family-handle]').forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (typeof onSelectFamilyOption === 'function') {
        onSelectFamilyOption(slotKey, button.dataset.routineFamilyHandle || '');
      }
    });
  });

  article.appendChild(imageLink);
  article.appendChild(content);

  requestAnimationFrame(() => {
    article.classList.remove('is-entering');
  });

  return article;
}

function updateRoutineCardStep(node, stepName, slotLabel) {
  let stepEl = node.querySelector('.build-my-routine-card__step');
  const text = stepName ? `${stepName} · ${slotLabel || ''}` : '';

  if (!stepEl && text) {
    stepEl = document.createElement('p');
    stepEl.className = 'build-my-routine-card__step';

    const content = node.querySelector('.build-my-routine-card__content');
    if (content) content.insertBefore(stepEl, content.firstChild);
  }

  if (stepEl) {
    stepEl.textContent = text;
    if (!text) stepEl.remove();
  }
}
function formatFilterLabelList(labels) {
  if (!labels.length) return '';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

function buildMakeupEmptyNotice(onClearBrandFilter) {
  const notice = document.createElement('aside');
  notice.className = 'build-my-routine-makeup-empty';
  notice.setAttribute('role', 'status');
  notice.setAttribute('aria-live', 'polite');

  const icon = document.createElement('span');
  icon.className = 'build-my-routine-makeup-empty__icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.textContent = '\u2022';
  notice.appendChild(icon);

  const body = document.createElement('div');
  body.className = 'build-my-routine-makeup-empty__body';

  const title = document.createElement('h4');
  title.className = 'build-my-routine-makeup-empty__title';
  body.appendChild(title);

  const message = document.createElement('p');
  message.className = 'build-my-routine-makeup-empty__message';
  body.appendChild(message);

  const actions = document.createElement('div');
  actions.className = 'build-my-routine-makeup-empty__actions';

  const clearBtn = document.createElement('button');
  clearBtn.type = 'button';
  clearBtn.className = 'build-my-routine-secondary build-my-routine-makeup-empty__clear';
  clearBtn.textContent = 'Clear brand filter';

  if (typeof onClearBrandFilter === 'function') {
    clearBtn.addEventListener('click', onClearBrandFilter);
  }

  actions.appendChild(clearBtn);
  body.appendChild(actions);
  notice.appendChild(body);

  return { notice, title, message, clearBtn };
}

function updateMakeupEmptyNoticeCopy(parts, products, optionSets) {
  const diag = diagnoseMakeupEmptyFromProducts(products);
  const hasBrandFilter = !state.preferred_brands.includes('any') && state.preferred_brands.length > 0;
  const brandLabels = hasBrandFilter ? brandLabelsForSlugs(state.preferred_brands, optionSets) : [];
  const brandList = formatBrandList(brandLabels);

  parts.title.textContent = 'No makeup picks for your selection';
  parts.clearBtn.hidden = true;

  if (diag.kind === 'no_catalog') {
    parts.message.textContent = 'We don’t currently stock any makeup in this catalogue. Continue with skincare only.';
    return;
  }

  if (diag.kind === 'brand_no_vendors') {
    parts.message.textContent = brandLabels.length === 1
      ? `The brand you picked (${brandList}) doesn’t stock makeup. Clear the brand filter to include makeup from other vendors, or continue with skincare only.`
      : `The brands you picked (${brandList}) don’t stock makeup. Clear the brand filter to include makeup from other vendors, or continue with skincare only.`;
    parts.clearBtn.hidden = false;
    return;
  }

  if (diag.kind === 'other_filters' || diag.kind === 'other_filters_no_brand') {
    const filterList = formatFilterLabelList(diag.unblockers || []);
    const filterPhrase = filterList
      ? `Your ${filterList} selection${(diag.unblockers && diag.unblockers.length === 1) ? '' : 's'}`
      : 'Your current selections';

    let body;

    if (diag.kind === 'other_filters') {
      body = brandLabels.length === 1
        ? `${filterPhrase} rule out every makeup product from ${brandList}. Try widening those answers on the left`
        : `${filterPhrase} rule out every makeup product from the brands you picked (${brandList}). Try widening those answers on the left`;

      if (diag.clearingBrandHelps) {
        body += ', or clear the brand filter to see makeup from other vendors';
        parts.clearBtn.hidden = false;
      }

      body += ' — or continue with skincare only.';
    } else {
      body = `${filterPhrase} rule out every makeup product. Try widening those answers on the left — or continue with skincare only.`;
    }

    parts.message.textContent = body;
    return;
  }

  if (diag.kind === 'no_subcategory_match') {
    parts.message.textContent = hasBrandFilter
      ? `The brand${brandLabels.length === 1 ? '' : 's'} you picked (${brandList}) don’t carry mascara, palette, color balm, foundation, or lip gloss that match your other selections. Clear the brand filter to see makeup from other vendors, or continue with skincare only.`
      : 'We don’t have mascara, palette, color balm, foundation, or lip gloss that match your current selections. Try widening your answers on the left, or continue with skincare only.';

    if (hasBrandFilter) parts.clearBtn.hidden = false;
    return;
  }

  parts.message.textContent = 'We couldn’t find any makeup that matches your current selections. Try widening your answers on the left, or continue with skincare only.';
}

console.log('[BuildMyRoutine] render loaded');